/**
 * Learner Storage – Standardisierte localStorage-Schnittstelle für Lernapp
 *
 * Diese Datei definiert:
 * - localStorage-Keys und Namespacing-Konvention
 * - Versionierungssystem für Schema-Migrationen
 * - Schnittstelle für Storage-Operationen
 */

import { LearnerCourseProgress, SchemaVersion } from '../models/lernapp.models';
import { courseIdStorageAliases, normalizeCourseId } from '../course-id.aliases';

/**
 * Schema-Versionen für zukünftige Migrationen
 */
export const STORAGE_SCHEMA_VERSIONS = Object.freeze({
  v1: { major: 1, minor: 0, patch: 0 } as SchemaVersion, // Erste Version mit neuen Modellen
  current: { major: 1, minor: 0, patch: 0 } as SchemaVersion, // Aktuelle Version
});

/**
 * localStorage Key-Präfixe für Namespacing
 */
export const STORAGE_KEY_PREFIXES = Object.freeze({
  learnerState: 'lernapp_state_', // lernapp_state_{courseId}
  courseProgress: 'lernapp_progress_', // lernapp_progress_{courseId}
  preferences: 'lernapp_preferences', // Globale Präferenzen
  metadata: 'lernapp_metadata_', // Metadaten pro Kurs: lernapp_metadata_{courseId}
  activity: 'lernapp_activity_', // Legacy-Aktivitaet pro Kurs: lernapp_activity_{courseId}
});

/**
 * Metadaten-Keys (pro Kurs)
 */
export const STORAGE_METADATA_KEYS = Object.freeze({
  schemaVersion: 'schemaVersion', // Aktuelle Schema-Version des Kurses
  lastMigration: 'lastMigration', // Timestamp der letzten Migration
  legacyConverted: 'legacyConverted', // Boolean: wurde von altem Format konvertiert?
});

/**
 * Preferences-Keys (global)
 */
export const STORAGE_PREFERENCES_KEYS = Object.freeze({
  language: 'language', // de | en
  theme: 'theme', // dark | light
  sidebarCollapsed: 'sidebarCollapsed', // boolean
  lastOpenedCourseId: 'lastOpenedCourseId', // Letzter Kurs
  overviewOnboardingDecided: 'overviewOnboardingDecided', // Begrüßungsentscheidung getroffen
});

/**
 * Liefert den Storage-Key für den Fortschritt eines Kurses
 */
export function getCourseProgressKey(courseId: string): string {
  return `${STORAGE_KEY_PREFIXES.courseProgress}${courseId}`;
}

/**
 * Liefert den Storage-Key fuer den versionierten Lernzustand eines Kurses
 */
export function getLearnerStateKey(courseId: string): string {
  return `${STORAGE_KEY_PREFIXES.learnerState}${courseId}`;
}

/**
 * Liefert den Storage-Key für Metadaten eines Kurses
 */
export function getCourseMetadataKey(courseId: string): string {
  return `${STORAGE_KEY_PREFIXES.metadata}${courseId}`;
}

/**
 * Liefert den alten Storage-Key fuer Aktivitaet eines Kurses
 */
export function getLegacyCourseActivityKey(courseId: string): string {
  return `${STORAGE_KEY_PREFIXES.activity}${courseId}`;
}

/**
 * Storage-Operationen für Learner-Fortschritt
 */
export interface ILearnerStorage {
  /**
   * Laedt den versionierten Lernzustand fuer einen Kurs.
   */
  loadLearnerStateContainer(courseId: string): LearnerStateContainer | null;

  /**
   * Speichert den versionierten Lernzustand fuer einen Kurs.
   */
  saveLearnerStateContainer(container: LearnerStateContainer): void;

  /**
   * Lädt den Fortschritt für einen Kurs
   * Gibt null zurück wenn kein Fortschritt vorhanden
   */
  loadCourseProgress(courseId: string): LearnerCourseProgress | null;

  /**
   * Speichert den Fortschritt für einen Kurs
   */
  saveCourseProgress(progress: LearnerCourseProgress): void;

  /**
   * Laedt Activity-State fuer einen Kurs aus dem versionierten Container.
   */
  loadCourseActivity(courseId: string): Record<string, unknown> | null;

  /**
   * Speichert Activity-State fuer einen Kurs in den versionierten Container.
   */
  saveCourseActivity(courseId: string, activity: Record<string, unknown>): void;

  /**
   * Löscht den Fortschritt für einen Kurs (z.B. bei Reset/Neustart)
   */
  deleteCourseProgress(courseId: string): void;

  /**
   * Liefert Metadaten für einen Kurs (Versionierung, Migration-Status)
   */
  getCourseMetadata(courseId: string): CourseLearnerMetadata | null;

  /**
   * Speichert Metadaten für einen Kurs
   */
  setCourseMetadata(courseId: string, metadata: CourseLearnerMetadata): void;

  /**
   * Lädt globale Nutzer-Präferenzen
   */
  loadPreferences(): LearnerPreferences;

  /**
   * Speichert globale Nutzer-Präferenzen
   */
  savePreferences(prefs: LearnerPreferences): void;

  /**
   * Liefert alle Kurse mit gespeichertem Fortschritt
   */
  listSavedCourses(): string[];

  /**
   * Migriert alten Fortschritt zu neuem Format (wird in Phase 2 implementiert)
   */
  migrateFromLegacyFormat(courseId: string, legacyData: unknown): LearnerCourseProgress | null;
}

/**
 * Metadaten für Learner-Fortschritt eines Kurses
 */
export interface CourseLearnerMetadata {
  courseId: string;
  schemaVersion: SchemaVersion;
  lastMigration?: Date;
  legacyConverted: boolean; // War dies vom alten Format migriert?
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Globale Learner-Präferenzen
 */
export interface LearnerPreferences {
  language: 'de' | 'en';
  theme?: 'dark' | 'light';
  sidebarCollapsed: boolean;
  sidebarPanelWidth?: number;
  lastOpenedCourseId?: string;
  overviewOnboardingDecided?: boolean;
}

export interface LearnerStateSyncState {
  status: 'local-only' | 'pending' | 'synced' | 'error';
  lastSyncedAt?: Date;
  error?: string;
}

/**
 * Versionierter, kursbezogener Container fuer lokalen Lernzustand.
 *
 * `skills` und `evidence` bleiben in APP-A46 absichtlich leer strukturiert. Sie bilden nur
 * den spaeteren Vertrag fuer Skill-/Nachweislogik vor.
 */
export interface LearnerStateContainer {
  schemaVersion: number;
  courseId: string;
  contentVersion: string | null;
  createdAt: Date;
  updatedAt: Date;
  progress: LearnerCourseProgress | null;
  activity: Record<string, unknown>;
  tasks: LearnerCourseProgress['tasks'];
  review: Record<string, unknown>;
  skills: Record<string, unknown>;
  evidence: unknown[];
  sync: LearnerStateSyncState;
  metadata?: CourseLearnerMetadata;
  preferences?: Record<string, unknown> | null;
}

const LEARNER_STATE_SCHEMA_VERSION = 1;

/**
 * Standardimplementierung von ILearnerStorage mit localStorage
 */
export class LocalStorageLearnerStorage implements ILearnerStorage {
  private readonly storage: Storage;

  constructor(storage: Storage = globalThis?.localStorage) {
    this.storage = storage;
  }

  loadLearnerStateContainer(courseId: string): LearnerStateContainer | null {
    const normalizedCourseId = normalizeCourseId(courseId);
    const existing = this.loadStoredContainer(normalizedCourseId);
    const aliased = this.loadAliasedStoredContainers(normalizedCourseId);
    const migrated = this.migrateLegacyStateContainer(normalizedCourseId);
    const candidates = [existing, ...aliased, migrated].filter(
      (candidate): candidate is LearnerStateContainer => candidate !== null,
    );
    if (candidates.length === 0) {
      return null;
    }
    if (candidates.length === 1 && existing) {
      return existing;
    }

    const reconciled = this.reconcileLearnerStateContainers(normalizedCourseId, candidates);
    this.saveLearnerStateContainer(reconciled);
    return reconciled;
  }

  saveLearnerStateContainer(container: LearnerStateContainer): void {
    const courseId = normalizeCourseId(container.courseId);
    const normalizedContainer = this.withContainerCourseId(container, courseId);
    const key = getLearnerStateKey(courseId);
    try {
      this.storage.setItem(key, JSON.stringify(normalizedContainer));
    } catch (error) {
      console.error(`Failed to save learner state for ${courseId}:`, error);
    }
  }

  loadCourseProgress(courseId: string): LearnerCourseProgress | null {
    return this.loadLearnerStateContainer(courseId)?.progress ?? null;
  }

  saveCourseProgress(progress: LearnerCourseProgress): void {
    const courseId = normalizeCourseId(progress.courseId);
    const normalizedProgress = this.withProgressCourseId(progress, courseId);
    try {
      const now = new Date();
      const existing = this.loadLearnerStateContainer(courseId);
      const existingMetadata = existing?.metadata;
      const metadata: CourseLearnerMetadata = {
        courseId,
        schemaVersion: STORAGE_SCHEMA_VERSIONS.current,
        lastMigration: existingMetadata?.lastMigration,
        legacyConverted: existingMetadata?.legacyConverted ?? false,
        createdAt: existingMetadata?.createdAt ?? now,
        updatedAt: now,
      };
      this.saveLearnerStateContainer({
        ...this.createEmptyContainer(courseId, now),
        ...existing,
        courseId,
        updatedAt: now,
        progress: normalizedProgress,
        tasks: normalizedProgress.tasks,
        metadata,
      });
    } catch (error) {
      console.error(`Failed to save course progress for ${courseId}:`, error);
    }
  }

  loadCourseActivity(courseId: string): Record<string, unknown> | null {
    return this.loadLearnerStateContainer(courseId)?.activity ?? null;
  }

  saveCourseActivity(courseId: string, activity: Record<string, unknown>): void {
    const normalizedCourseId = normalizeCourseId(courseId);
    const now = new Date();
    const existing = this.loadLearnerStateContainer(normalizedCourseId);
    this.saveLearnerStateContainer({
      ...this.createEmptyContainer(normalizedCourseId, now),
      ...existing,
      courseId: normalizedCourseId,
      updatedAt: now,
      activity,
      review: isRecord(activity['review']) ? activity['review'] : {},
    });
  }

  deleteCourseProgress(courseId: string): void {
    this.storage.removeItem(getLearnerStateKey(normalizeCourseId(courseId)));
  }

  getCourseMetadata(courseId: string): CourseLearnerMetadata | null {
    return this.loadLearnerStateContainer(courseId)?.metadata ?? null;
  }

  setCourseMetadata(courseId: string, metadata: CourseLearnerMetadata): void {
    const normalizedCourseId = normalizeCourseId(courseId);
    const now = new Date();
    const existing = this.loadLearnerStateContainer(normalizedCourseId);
    this.saveLearnerStateContainer({
      ...this.createEmptyContainer(normalizedCourseId, now),
      ...existing,
      courseId: normalizedCourseId,
      updatedAt: now,
      metadata: this.withMetadataCourseId(metadata, normalizedCourseId),
    });
  }

  loadPreferences(): LearnerPreferences {
    const json = this.storage.getItem(STORAGE_KEY_PREFIXES.preferences);

    if (!json) {
      // Gib Defaults zurück
      return {
        language: 'de',
        theme: 'light',
        sidebarCollapsed: false,
      };
    }

    try {
      return this.normalizePreferences(JSON.parse(json) as LearnerPreferences, true);
    } catch (error) {
      console.error('Failed to parse preferences:', error);
      return {
        language: 'de',
        theme: 'light',
        sidebarCollapsed: false,
      };
    }
  }

  savePreferences(prefs: LearnerPreferences): void {
    const json = JSON.stringify(this.normalizePreferences(prefs, false));

    try {
      this.storage.setItem(STORAGE_KEY_PREFIXES.preferences, json);
    } catch (error) {
      console.error('Failed to save preferences:', error);
    }
  }

  listSavedCourses(): string[] {
    const courses = new Set<string>();
    const prefix = STORAGE_KEY_PREFIXES.courseProgress;

    // Iterate through all keys in localStorage
    for (let i = 0; i < (this.storage?.length ?? 0); i++) {
      const key = this.storage?.key(i);
      if (key?.startsWith(STORAGE_KEY_PREFIXES.learnerState)) {
        const courseId = key.substring(STORAGE_KEY_PREFIXES.learnerState.length);
        courses.add(normalizeCourseId(courseId));
      } else if (key?.startsWith(prefix)) {
        const courseId = key.substring(prefix.length);
        courses.add(normalizeCourseId(courseId));
      }
    }

    return [...courses];
  }

  migrateFromLegacyFormat(courseId: string, legacyData: unknown): LearnerCourseProgress | null {
    // Wird in Phase 2 implementiert
    console.warn('Legacy migration not yet implemented', courseId, legacyData);
    return null;
  }

  private loadStoredContainer(courseId: string): LearnerStateContainer | null {
    const json = this.storage.getItem(getLearnerStateKey(courseId));
    if (!json) {
      return null;
    }
    try {
      return deserializeLearnerStateContainer(JSON.parse(json), courseId);
    } catch (error) {
      console.error(`Failed to parse learner state for ${courseId}:`, error);
      return null;
    }
  }

  private migrateLegacyStateContainer(courseId: string): LearnerStateContainer | null {
    const normalizedCourseId = normalizeCourseId(courseId);
    const candidates = courseIdStorageAliases(normalizedCourseId);
    const containers: LearnerStateContainer[] = [];
    for (const candidateCourseId of candidates) {
      const legacyProgress = this.loadLegacyCourseProgress(candidateCourseId);
      const legacyMetadata = this.loadLegacyCourseMetadata(candidateCourseId);
      const legacyActivity = this.loadLegacyCourseActivity(candidateCourseId);
      const legacyPreferences = this.loadLegacyPreferences();

      if (!legacyProgress && !legacyMetadata && !legacyActivity) {
        continue;
      }

      const progress = legacyProgress
        ? this.withProgressCourseId(legacyProgress, normalizedCourseId)
        : null;
      const metadata = legacyMetadata
        ? this.withMetadataCourseId(legacyMetadata, normalizedCourseId)
        : undefined;
      const fallbackDate = new Date(0);
      containers.push({
        ...this.createEmptyContainer(normalizedCourseId, fallbackDate),
        createdAt: metadata?.createdAt ?? progress?.startedAt ?? fallbackDate,
        updatedAt: metadata?.updatedAt ?? progress?.lastAccessedAt ?? fallbackDate,
        progress,
        activity: legacyActivity ?? {},
        tasks: progress?.tasks ?? [],
        review: isRecord(legacyActivity?.['review']) ? legacyActivity['review'] : {},
        metadata,
        preferences: legacyPreferences ? this.normalizePreferenceRecord(legacyPreferences) : null,
      });
    }

    return containers.length > 0
      ? this.reconcileLearnerStateContainers(normalizedCourseId, containers)
      : null;
  }

  private loadLegacyCourseProgress(courseId: string): LearnerCourseProgress | null {
    const json = this.storage.getItem(getCourseProgressKey(courseId));
    if (!json) {
      return null;
    }

    try {
      const progress = deserializeCourseProgress(JSON.parse(json));
      return progress?.courseId === courseId ? progress : null;
    } catch {
      return null;
    }
  }

  private loadLegacyCourseMetadata(courseId: string): CourseLearnerMetadata | null {
    const json = this.storage.getItem(getCourseMetadataKey(courseId));
    if (!json) {
      return null;
    }

    try {
      return deserializeCourseMetadata(JSON.parse(json), courseId);
    } catch {
      return null;
    }
  }

  private loadLegacyCourseActivity(courseId: string): Record<string, unknown> | null {
    const json = this.storage.getItem(getLegacyCourseActivityKey(courseId));
    if (!json) {
      return null;
    }

    try {
      const parsed = JSON.parse(json);
      return isRecord(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  private loadLegacyPreferences(): Record<string, unknown> | null {
    const json = this.storage.getItem(STORAGE_KEY_PREFIXES.preferences);
    if (!json) {
      return null;
    }

    try {
      const parsed = JSON.parse(json);
      return isRecord(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  private createEmptyContainer(courseId: string, now: Date): LearnerStateContainer {
    return {
      schemaVersion: LEARNER_STATE_SCHEMA_VERSION,
      courseId,
      contentVersion: null,
      createdAt: now,
      updatedAt: now,
      progress: null,
      activity: {},
      tasks: [],
      review: {},
      skills: {},
      evidence: [],
      sync: { status: 'local-only' },
    };
  }

  private loadAliasedStoredContainers(courseId: string): LearnerStateContainer[] {
    const aliases = courseIdStorageAliases(courseId).filter((alias) => alias !== courseId);
    const containers: LearnerStateContainer[] = [];
    for (const alias of aliases) {
      const container = this.loadStoredContainer(alias);
      if (container) {
        containers.push(this.withContainerCourseId(container, courseId));
      }
    }
    return containers;
  }

  private reconcileLearnerStateContainers(
    courseId: string,
    containers: LearnerStateContainer[],
  ): LearnerStateContainer {
    const newest = [...containers].sort(
      (left, right) => right.updatedAt.getTime() - left.updatedAt.getTime(),
    )[0];
    const progress = [...containers]
      .map((container) => container.progress)
      .filter((candidate): candidate is LearnerCourseProgress => candidate !== null)
      .sort((left, right) => right.lastAccessedAt.getTime() - left.lastAccessedAt.getTime())[0]
      ?? null;
    const metadata = [...containers]
      .map((container) => container.metadata)
      .filter((candidate): candidate is CourseLearnerMetadata => candidate !== undefined)
      .sort((left, right) => right.updatedAt.getTime() - left.updatedAt.getTime())[0];
    const activity = this.mergeContainerRecords(containers, 'activity');
    const review = isRecord(activity['review'])
      ? activity['review']
      : this.mergeContainerRecords(containers, 'review');
    const preferences = [...containers]
      .sort((left, right) => right.updatedAt.getTime() - left.updatedAt.getTime())
      .find((container) => container.preferences && Object.keys(container.preferences).length > 0)
      ?.preferences ?? newest.preferences;

    return this.withContainerCourseId({
      ...newest,
      courseId,
      createdAt: new Date(Math.min(...containers.map((container) => container.createdAt.getTime()))),
      updatedAt: new Date(Math.max(...containers.map((container) => container.updatedAt.getTime()))),
      progress,
      tasks: progress?.tasks ?? newest.tasks,
      activity,
      review,
      metadata,
      preferences: preferences ? this.normalizePreferenceRecord(preferences) : preferences,
    }, courseId);
  }

  private mergeContainerRecords(
    containers: LearnerStateContainer[],
    field: 'activity' | 'review',
  ): Record<string, unknown> {
    return [...containers]
      .sort((left, right) => left.updatedAt.getTime() - right.updatedAt.getTime())
      .reduce<Record<string, unknown>>(
        (merged, container) => ({ ...merged, ...container[field] }),
        {},
      );
  }

  private withContainerCourseId(
    container: LearnerStateContainer,
    courseId: string,
  ): LearnerStateContainer {
    return {
      ...container,
      courseId,
      progress: container.progress ? this.withProgressCourseId(container.progress, courseId) : null,
      tasks: container.progress?.tasks ?? container.tasks,
      metadata: container.metadata
        ? this.withMetadataCourseId(container.metadata, courseId)
        : undefined,
      preferences: container.preferences
        ? this.normalizePreferenceRecord(container.preferences)
        : container.preferences,
    };
  }

  private withProgressCourseId(
    progress: LearnerCourseProgress,
    courseId: string,
  ): LearnerCourseProgress {
    return { ...progress, courseId: courseId as LearnerCourseProgress['courseId'] };
  }

  private withMetadataCourseId(
    metadata: CourseLearnerMetadata,
    courseId: string,
  ): CourseLearnerMetadata {
    return { ...metadata, courseId };
  }

  private normalizePreferences(prefs: LearnerPreferences, persist: boolean): LearnerPreferences {
    const normalized = prefs.lastOpenedCourseId
      ? { ...prefs, lastOpenedCourseId: normalizeCourseId(prefs.lastOpenedCourseId) }
      : prefs;
    if (persist && normalized.lastOpenedCourseId !== prefs.lastOpenedCourseId) {
      try {
        this.storage.setItem(STORAGE_KEY_PREFIXES.preferences, JSON.stringify(normalized));
      } catch (error) {
        console.error('Failed to migrate preferences:', error);
      }
    }
    return normalized;
  }

  private normalizePreferenceRecord(
    prefs: Record<string, unknown>,
  ): Record<string, unknown> {
    if (typeof prefs['lastOpenedCourseId'] !== 'string') {
      return prefs;
    }
    const normalizedCourseId = normalizeCourseId(prefs['lastOpenedCourseId']);
    return normalizedCourseId === prefs['lastOpenedCourseId']
      ? prefs
      : { ...prefs, lastOpenedCourseId: normalizedCourseId };
  }
}

/**
 * Test-freundliche Implementierung mit In-Memory Storage
 */
export class MemoryLearnerStorage implements ILearnerStorage {
  private data: Map<string, string> = new Map();

  loadLearnerStateContainer(courseId: string): LearnerStateContainer | null {
    const normalizedCourseId = normalizeCourseId(courseId);
    const json = this.data.get(getLearnerStateKey(normalizedCourseId));
    if (!json) {
      return null;
    }

    try {
      return deserializeLearnerStateContainer(JSON.parse(json), normalizedCourseId);
    } catch {
      return null;
    }
  }

  saveLearnerStateContainer(container: LearnerStateContainer): void {
    const courseId = normalizeCourseId(container.courseId);
    this.data.set(getLearnerStateKey(courseId), JSON.stringify({
      ...container,
      courseId,
      progress: container.progress ? { ...container.progress, courseId } : null,
      metadata: container.metadata ? { ...container.metadata, courseId } : undefined,
    }));
  }

  loadCourseProgress(courseId: string): LearnerCourseProgress | null {
    return this.loadLearnerStateContainer(courseId)?.progress ?? null;
  }

  saveCourseProgress(progress: LearnerCourseProgress): void {
    const courseId = normalizeCourseId(progress.courseId);
    const normalizedProgress = {
      ...progress,
      courseId: courseId as LearnerCourseProgress['courseId'],
    };
    const existingMetadata = this.getCourseMetadata(courseId);
    const now = new Date();
    const metadata: CourseLearnerMetadata = {
      courseId,
      schemaVersion: STORAGE_SCHEMA_VERSIONS.current,
      lastMigration: existingMetadata?.lastMigration,
      legacyConverted: existingMetadata?.legacyConverted ?? false,
      createdAt: existingMetadata?.createdAt ?? now,
      updatedAt: now,
    };
    const existing = this.loadLearnerStateContainer(courseId);
    this.saveLearnerStateContainer({
      ...createMemoryEmptyContainer(courseId, now),
      ...existing,
      updatedAt: now,
      progress: normalizedProgress,
      tasks: normalizedProgress.tasks,
      metadata,
    });
  }

  loadCourseActivity(courseId: string): Record<string, unknown> | null {
    return this.loadLearnerStateContainer(courseId)?.activity ?? null;
  }

  saveCourseActivity(courseId: string, activity: Record<string, unknown>): void {
    const normalizedCourseId = normalizeCourseId(courseId);
    const now = new Date();
    const existing = this.loadLearnerStateContainer(normalizedCourseId);
    this.saveLearnerStateContainer({
      ...createMemoryEmptyContainer(normalizedCourseId, now),
      ...existing,
      updatedAt: now,
      activity,
      review: isRecord(activity['review']) ? activity['review'] : {},
    });
  }

  deleteCourseProgress(courseId: string): void {
    this.data.delete(getLearnerStateKey(normalizeCourseId(courseId)));
  }

  getCourseMetadata(courseId: string): CourseLearnerMetadata | null {
    return this.loadLearnerStateContainer(courseId)?.metadata ?? null;
  }

  setCourseMetadata(courseId: string, metadata: CourseLearnerMetadata): void {
    const normalizedCourseId = normalizeCourseId(courseId);
    const now = new Date();
    const existing = this.loadLearnerStateContainer(normalizedCourseId);
    this.saveLearnerStateContainer({
      ...createMemoryEmptyContainer(normalizedCourseId, now),
      ...existing,
      updatedAt: now,
      metadata: { ...metadata, courseId: normalizedCourseId },
    });
  }

  loadPreferences(): LearnerPreferences {
    const json = this.data.get(STORAGE_KEY_PREFIXES.preferences);

    if (!json) {
      return { language: 'de', theme: 'light', sidebarCollapsed: false };
    }

    try {
      return normalizeLearnerPreferences(JSON.parse(json) as LearnerPreferences);
    } catch (error) {
      return { language: 'de', theme: 'light', sidebarCollapsed: false };
    }
  }

  savePreferences(prefs: LearnerPreferences): void {
    this.data.set(STORAGE_KEY_PREFIXES.preferences, JSON.stringify(normalizeLearnerPreferences(prefs)));
  }

  listSavedCourses(): string[] {
    const courses: string[] = [];
    const prefix = STORAGE_KEY_PREFIXES.learnerState;

    for (const key of this.data.keys()) {
      if (key.startsWith(prefix)) {
        const courseId = key.substring(prefix.length);
        const normalizedCourseId = normalizeCourseId(courseId);
        if (!courses.includes(normalizedCourseId)) {
          courses.push(normalizedCourseId);
        }
      }
    }

    return courses;
  }

  migrateFromLegacyFormat(courseId: string, legacyData: unknown): LearnerCourseProgress | null {
    return null; // Wird in Phase 2 implementiert
  }
}

function deserializeCourseProgress(value: unknown): LearnerCourseProgress | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const progress = value as LearnerCourseProgress;
  if (
    typeof progress.courseId !== 'string' ||
    !Array.isArray(progress.moduleProgress) ||
    !Array.isArray(progress.tasks)
  ) {
    return null;
  }

  const startedAt = deserializeDate(progress.startedAt);
  const lastAccessedAt = deserializeDate(progress.lastAccessedAt);
  if (!startedAt || !lastAccessedAt) {
    return null;
  }
  progress.startedAt = startedAt;
  progress.lastAccessedAt = lastAccessedAt;

  for (const moduleProgress of progress.moduleProgress) {
    if (!Array.isArray(moduleProgress.lectionProgress)) {
      return null;
    }
    if (moduleProgress.milestoneCompletedAt !== undefined) {
      const milestoneCompletedAt = deserializeDate(moduleProgress.milestoneCompletedAt);
      if (!milestoneCompletedAt) {
        return null;
      }
      moduleProgress.milestoneCompletedAt = milestoneCompletedAt;
    }

    for (const lectionProgress of moduleProgress.lectionProgress) {
      if (!Array.isArray(lectionProgress.lernbausteinProgress)) {
        return null;
      }
      for (const lernbausteinProgress of lectionProgress.lernbausteinProgress) {
        if (lernbausteinProgress.viewedAt !== undefined) {
          const viewedAt = deserializeDate(lernbausteinProgress.viewedAt);
          if (!viewedAt) {
            return null;
          }
          lernbausteinProgress.viewedAt = viewedAt;
        }
      }
    }
  }

  for (const task of progress.tasks) {
    if (task.startedAt !== undefined) {
      const startedAt = deserializeDate(task.startedAt);
      if (!startedAt) {
        return null;
      }
      task.startedAt = startedAt;
    }
    if (task.completedAt !== undefined) {
      const completedAt = deserializeDate(task.completedAt);
      if (!completedAt) {
        return null;
      }
      task.completedAt = completedAt;
    }
  }

  return progress;
}

function deserializeCourseMetadata(
  value: unknown,
  expectedCourseId: string,
): CourseLearnerMetadata | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const metadata = value as CourseLearnerMetadata;
  if (metadata.courseId !== expectedCourseId || !metadata.schemaVersion) {
    return null;
  }

  const createdAt = deserializeDate(metadata.createdAt);
  const updatedAt = deserializeDate(metadata.updatedAt);
  if (!createdAt || !updatedAt) {
    return null;
  }
  metadata.createdAt = createdAt;
  metadata.updatedAt = updatedAt;

  if (metadata.lastMigration !== undefined) {
    const lastMigration = deserializeDate(metadata.lastMigration);
    if (!lastMigration) {
      return null;
    }
    metadata.lastMigration = lastMigration;
  }

  return metadata;
}

function deserializeLearnerStateContainer(
  value: unknown,
  expectedCourseId: string,
): LearnerStateContainer | null {
  if (!isRecord(value) || value['courseId'] !== expectedCourseId) {
    return null;
  }

  const createdAt = deserializeDate(value['createdAt']);
  const updatedAt = deserializeDate(value['updatedAt']);
  if (!createdAt || !updatedAt || typeof value['schemaVersion'] !== 'number') {
    return null;
  }

  const progress = value['progress'] === null || value['progress'] === undefined
    ? null
    : deserializeCourseProgress(value['progress']);
  if (progress && progress.courseId !== expectedCourseId) {
    return null;
  }

  const metadata = value['metadata'] === undefined
    ? undefined
    : deserializeCourseMetadata(value['metadata'], expectedCourseId) ?? undefined;

  const sync: Record<string, unknown> = isRecord(value['sync'])
    ? { ...value['sync'] }
    : { status: 'local-only' };
  if (sync['lastSyncedAt'] !== undefined) {
    const lastSyncedAt = deserializeDate(sync['lastSyncedAt']);
    if (lastSyncedAt) {
      sync['lastSyncedAt'] = lastSyncedAt;
    } else {
      delete sync['lastSyncedAt'];
    }
  }

  return {
    schemaVersion: value['schemaVersion'],
    courseId: expectedCourseId,
    contentVersion: typeof value['contentVersion'] === 'string' ? value['contentVersion'] : null,
    createdAt,
    updatedAt,
    progress,
    activity: isRecord(value['activity']) ? value['activity'] : {},
    tasks: progress?.tasks ?? [],
    review: isRecord(value['review']) ? value['review'] : {},
    skills: isRecord(value['skills']) ? value['skills'] : {},
    evidence: Array.isArray(value['evidence']) ? value['evidence'] : [],
    sync: {
      status: isValidSyncStatus(sync['status']) ? sync['status'] : 'local-only',
      lastSyncedAt: sync['lastSyncedAt'] instanceof Date ? sync['lastSyncedAt'] : undefined,
      error: typeof sync['error'] === 'string' ? sync['error'] : undefined,
    },
    metadata,
    preferences: isRecord(value['preferences']) ? value['preferences'] : null,
  };
}

function createMemoryEmptyContainer(courseId: string, now: Date): LearnerStateContainer {
  return {
    schemaVersion: LEARNER_STATE_SCHEMA_VERSION,
    courseId,
    contentVersion: null,
    createdAt: now,
    updatedAt: now,
    progress: null,
    activity: {},
    tasks: [],
    review: {},
    skills: {},
    evidence: [],
    sync: { status: 'local-only' },
  };
}

function normalizeLearnerPreferences(prefs: LearnerPreferences): LearnerPreferences {
  if (!prefs.lastOpenedCourseId) {
    return prefs;
  }
  return {
    ...prefs,
    lastOpenedCourseId: normalizeCourseId(prefs.lastOpenedCourseId),
  };
}

function deserializeDate(value: unknown): Date | null {
  if (!(typeof value === 'string' || value instanceof Date)) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function isValidSyncStatus(value: unknown): value is LearnerStateSyncState['status'] {
  return value === 'local-only' || value === 'pending' || value === 'synced' || value === 'error';
}
